// src/services/ptaeService.mjs
import BaseApiService from "./baseApiService.mjs";


class PTAEService extends BaseApiService {
  constructor() {
    super();
    this.baseURL = '/ptae';
  }

  async getPTAEs(columnA = '', search = '', includeSuspended = false, region = '') {
    try {
      const params = { columnA, search };
      if (includeSuspended) params.includeSuspended = 'true';
      // Region-scoped chart of accounts. Omitted means "the caller's own region"
      // for an admin, or every region for an auditor.
      if (region) params.region = region;
      const response = await this.api.get(this.baseURL, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch PTAEs');
    }
  }

  // The project → task → expense cascade for coding a claim. Award is omitted
  // as a step because project+task determines it; it comes back on each task.
  async getOptions(region = '') {
    try {
      const response = await this.api.get(`${this.baseURL}/options`, {
        params: region ? { region } : {},
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch PTAE options');
    }
  }

  // Finance director's approval queue (readable by managers so a requester can
  // see their own request waiting).
  async getChangeBatches(status = '', region = '') {
    try {
      const params = {};
      if (status) params.status = status;
      if (region) params.region = region;
      const response = await this.api.get(`${this.baseURL}/change-batches`, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch PTAE change requests');
    }
  }

  // Finance director only.
  async decideChangeBatch(batchId, decision, comments = '') {
    try {
      const response = await this.api.post(
        `${this.baseURL}/change-batches/${batchId}/decide`,
        { decision, comments }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to record decision');
    }
  }

  // How many PTAEs each region holds, and which region others fall back to.
  async getRegions() {
    try {
      const response = await this.api.get(`${this.baseURL}/regions`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch PTAE regions');
    }
  }

  // Bulk-load a region's Chart of Accounts export. Upserts, so re-uploading a
  // corrected report refreshes rows rather than duplicating them.
  async importPTAEs(file, region) {
    try {
      const form = new FormData();
      form.append('file', file);
      if (region) form.append('region', region);
      const response = await this.api.post(`${this.baseURL}/import`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to import PTAEs');
    }
  }

  // Every change below is a REQUEST awaiting the finance director, and every one
  // carries a reason - the backend rejects a blank one with a 400, so the forms
  // require it before calling.
  async addPTAE(id, description, reason = '', region = '') {
    try {
      const response = await this.api.post(this.baseURL, {
        id, description, reason, ...(region ? { region } : {}),
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to add PTAE');
    }
  }

  async updatePTAE(id, changes) {
    try {
      // Accepts a bare description (legacy call sites) or a payload that can
      // also rename the code itself via ptaeId. `reason` travels in the payload.
      const body = typeof changes === 'string' ? { description: changes } : changes;
      const response = await this.api.put(`${this.baseURL}/${encodeURIComponent(id)}`, body);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to request PTAE edit');
    }
  }

  async suspendPTAE(id, reason = '', region = '') {
    try {
      const response = await this.api.patch(
        `${this.baseURL}/${encodeURIComponent(id)}/suspend`,
        { reason, ...(region ? { region } : {}) }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to request suspension');
    }
  }

  async reactivatePTAE(id, reason = '', region = '') {
    try {
      const response = await this.api.patch(
        `${this.baseURL}/${encodeURIComponent(id)}/reactivate`,
        { reason, ...(region ? { region } : {}) }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to request reactivation');
    }
  }

  async getUniqueColumnAs() {
    try {
      const response = await this.api.get(`${this.baseURL}/unique-column-a`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch unique Column A values');
    }
  }
}

const ptaeService = new PTAEService();
export default ptaeService;